import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError, toApiError } from '@/lib/api'
import { useAnswerApi } from '@/hooks/useAnswerApi'

/** Load state for the current configurable system prompt. `submittedBy` /
 * `createdAt` are blank/null until an admin has saved a first version. */
export type SystemPromptState =
  | { kind: 'loading' }
  | { kind: 'ready'; prompt: string; submittedBy: string; createdAt: Date | null }
  | { kind: 'error'; message: string }

/** Save state for an in-flight or completed POST. Kept separate from the load
 * state so a failed save doesn't blank out the editor's loaded prompt. */
export type SystemPromptSaveState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved'; submittedBy: string; createdAt: Date | null }
  | { kind: 'error'; message: string }

export type UseSystemPromptOptions = {
  baseUrl: string
  acquireToken: () => Promise<string>
  fetchImpl?: typeof fetch
}

export type UseSystemPromptResult = {
  state: SystemPromptState
  saveState: SystemPromptSaveState
  refresh: () => Promise<void>
  save: (prompt: string) => Promise<void>
}

export function useSystemPrompt(options: UseSystemPromptOptions): UseSystemPromptResult {
  const [state, setState] = useState<SystemPromptState>({ kind: 'loading' })
  const [saveState, setSaveState] = useState<SystemPromptSaveState>({ kind: 'idle' })
  const abortRef = useRef<AbortController | null>(null)
  const api = useAnswerApi(options)

  const refresh = useCallback(async () => {
    const controller = new AbortController()
    abortRef.current?.abort()
    abortRef.current = controller
    try {
      const current = await api.getSystemPrompt({ signal: controller.signal })
      if (controller.signal.aborted) return
      setState({
        kind: 'ready',
        prompt: current.prompt,
        submittedBy: current.submittedBy,
        createdAt: current.createdAt,
      })
    } catch (err) {
      if (controller.signal.aborted) return
      const apiErr: ApiError = err instanceof ApiError ? err : await toApiError(err)
      setState((prev) => (prev.kind === 'ready' ? prev : { kind: 'error', message: apiErr.message }))
    } finally {
      if (abortRef.current === controller) abortRef.current = null
    }
  }, [api])

  const save = useCallback(
    async (prompt: string) => {
      setSaveState({ kind: 'saving' })
      try {
        const created = await api.updateSystemPrompt({
          systemPromptUpdateRequest: { prompt },
        })
        setSaveState({
          kind: 'saved',
          submittedBy: created.submittedBy,
          createdAt: created.createdAt,
        })
        setState({
          kind: 'ready',
          prompt: created.prompt,
          submittedBy: created.submittedBy,
          createdAt: created.createdAt,
        })
      } catch (err) {
        const apiErr: ApiError = err instanceof ApiError ? err : await toApiError(err)
        setSaveState({ kind: 'error', message: apiErr.message })
      }
    },
    [api],
  )

  // Load once on mount. Read `refresh` through a ref so consumers passing
  // inline acquireToken/fetchImpl closures (new identity each render) don't
  // re-trigger it in a render → fetch → setState loop.
  const refreshRef = useRef(refresh)
  useEffect(() => {
    refreshRef.current = refresh
  })
  useEffect(() => {
    void refreshRef.current()
    return () => {
      abortRef.current?.abort()
    }
  }, [])

  return { state, saveState, refresh, save }
}
